import { useEffect, useRef, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, signOut, useMe, useNavCounts, useOpenCompany } from '../lib/api';
import { Fox } from './Fox';

export const NAV: { path: string; label: string; badge?: 'awaiting' | 'rescue' | 'coaching' | 'soon' }[] = [
  { path: '/overview', label: 'Overview' },
  { path: '/conversations', label: 'Conversations', badge: 'awaiting' },
  { path: '/rescue', label: 'Rescue', badge: 'rescue' },
  { path: '/revive', label: 'Revive' },
  { path: '/coaching', label: 'Coaching', badge: 'coaching' },
  { path: '/leaderboard', label: 'Leaderboard' },
  { path: '/lead-sources', label: 'Lead sources', badge: 'soon' },
];


export function Shell() {
  const me = useMe();
  const counts = useNavCounts();
  const navigate = useNavigate();
  const location = useLocation();
  const main = useRef<HTMLElement>(null);

  // Each page starts at the top, like a full navigation.
  useEffect(() => {
    main.current?.scrollTo(0, 0);
  }, [location.pathname]);

  // A fresh install has no company yet: send the owner to Admin to add one.
  if (me.error instanceof ApiError && me.error.code === 'NO_COMPANY') return <Navigate to="/admin" replace />;

  return (
    <div className="app">
      <aside className="rail">
        <div className="brand">
          <span className="brand-row">
            <Fox size={30} ring />
            <span>
              Coach <span className="rex">Lex</span>
            </span>
          </span>
          <CompanySwitcher />
        </div>

        <button className={`ask-rex${location.pathname === '/ask' ? ' is-active' : ''}`} onClick={() => navigate('/ask')}>
          <Fox size={36} />
          <span className="ask-rex__text">
            <span className="ask-rex__title">Ask Lex</span>
            <span className="ask-rex__sub">Your AI sales coach</span>
          </span>
          <span className="ask-rex__spark">✦</span>
        </button>

        <nav className="nav" aria-label="Main">
          {NAV.map((n) => {
            const badge =
              n.badge === 'soon'
                ? 'Soon'
                : n.badge === 'awaiting' && counts.data?.awaitingReply
                  ? String(counts.data.awaitingReply)
                  : n.badge === 'rescue' && counts.data?.rescueOpen
                    ? String(counts.data.rescueOpen)
                    : n.badge === 'coaching' && counts.data?.coachingMoments
                      ? String(counts.data.coachingMoments)
                      : null;
            return (
              <NavLink key={n.path} to={n.path} className={({ isActive }) => (isActive ? 'active' : '')}>
                {n.label}
                {badge && <span className="nav-badge">{badge}</span>}
              </NavLink>
            );
          })}
        </nav>


        <UserCard />
      </aside>
      <main className="main" ref={main}>
        <Outlet />
      </main>
    </div>
  );
}

function UserCard() {
  const me = useMe();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const user = me.data?.user;
  return (
    <div className="user-card-wrap" ref={ref}>
      {open && (
        <div className="user-menu" role="menu">
          {me.data && me.data.user.role !== 'rep' && (
            <button role="menuitem" onClick={() => navigate('/admin')}>
              <span>Admin</span>
              <span className="hint">{user?.isOwner ? 'Companies & setup' : 'Team & setup'}</span>
            </button>
          )}
          <button role="menuitem" disabled>
            <span>Account settings</span>
            <span className="hint">Soon</span>
          </button>
          <button role="menuitem" className="danger" onClick={() => void signOut()}>
            <span>Sign out</span>
            {me.data?.signIn !== 'google' && <span className="hint">Shared password</span>}
          </button>
        </div>
      )}
      <button className="user-card" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="user-card__avatar">{user?.initials ?? ''}</span>
        <span className="user-card__text">
          <span className="user-card__name">{user?.name ?? ' '}</span>
          <span className="user-card__title">{user?.title ?? ''}</span>
        </span>
        <span className="user-card__caret">⌃</span>
      </button>
    </div>
  );
}

/** The company being viewed. Owners (and anyone in several companies) switch here. */
function CompanySwitcher() {
  const me = useMe();
  const open = useOpenCompany();
  const navigate = useNavigate();
  const list = me.data?.companies ?? [];
  if (list.length < 2) return <span className="brand-company">{me.data?.company.name ?? '\u00a0'}</span>;
  return (
    <select
      className="brand-company brand-company--select"
      aria-label="Company"
      value={me.data?.company.id}
      disabled={open.isPending}
      onChange={(e) => open.mutate(e.target.value, { onSuccess: () => navigate('/overview') })}
    >
      {list.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      ))}
    </select>
  );
}
